import * as React from "react";
import { Dog, Cat, Bird, PawPrint, Calendar, ChatText, Star, UserCircle } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { AiInsightCard } from "../AiInsightCard";
import type { AiInsight, CustomerDetailLabels, CustomerProfile, PetRecord } from "../types";
import {
  formatDateShort,
  formatTimeShort,
  mostBookedService,
  nextUpcomingAppointment,
  preferredStaffName,
} from "../utils";

interface Props {
  profile: CustomerProfile;
  labels: CustomerDetailLabels;
  aiInsight?: AiInsight;
  onJumpToPet?: (petId: string) => void;
}

const speciesIcon = { dog: Dog, cat: Cat, bird: Bird, other: PawPrint } as const;

export function OverviewTab({ profile, labels, aiInsight, onJumpToPet }: Props) {
  const { customer, pets, appointments } = profile;
  const next = nextUpcomingAppointment(appointments);
  const topService = mostBookedService(appointments);
  const staffName = preferredStaffName(profile);
  const noteFull = customer.internalNotes ?? "";
  const notePreview = noteFull.length > 160 ? `${noteFull.slice(0, 160)}…` : noteFull;
  const channel = customer.preferredContactChannel;

  return (
    <div className="grid gap-5 lg:grid-cols-12">
      {/* Left main column */}
      <div className="lg:col-span-8 space-y-5">
        {aiInsight && <AiInsightCard insight={aiInsight} dismissLabel={labels.overview.aiInsightDismiss} />}

        <section className="rounded-biz-card border border-biz-border bg-biz-surface p-5 shadow-biz-sm">
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-biz-foreground-subtle">
            {labels.overview.quickFactsTitle}
          </h2>
          <dl className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-4">
            <Fact
              icon={<UserCircle size={16} weight="regular" />}
              label={labels.overview.preferredChannelLabel}
              value={channel ? labels.preferredChannel[channel] : "—"}
            />
            <Fact
              icon={<Calendar size={16} weight="regular" />}
              label={labels.overview.nextAppointment("").replace(/[:：].*$/, "")}
              value={
                next
                  ? `${formatDateShort(next.startTime)} · ${formatTimeShort(next.startTime)} — ${next.serviceName}`
                  : labels.overview.noUpcoming
              }
              tone={next ? "primary" : "muted"}
            />
            <Fact
              icon={<Star size={16} weight="regular" />}
              label={labels.overview.mostBookedService}
              value={topService ?? "—"}
            />
            <Fact
              icon={<UserCircle size={16} weight="regular" />}
              label={labels.overview.preferredStaff}
              value={staffName ?? "—"}
            />
            {customer.firstVisitAt && (
              <Fact
                icon={<Calendar size={16} weight="regular" />}
                label={labels.overview.memberSince("").replace(/[:：].*$/, "")}
                value={formatDateShort(customer.firstVisitAt)}
              />
            )}
            {customer.lastVisitAt && (
              <Fact
                icon={<Calendar size={16} weight="regular" />}
                label={labels.overview.lastVisit("").replace(/[:：].*$/, "")}
                value={formatDateShort(customer.lastVisitAt)}
              />
            )}
          </dl>
        </section>

        <section className="rounded-biz-card border border-biz-border bg-biz-surface p-5 shadow-biz-sm">
          <div className="flex items-center gap-2 text-sm font-display font-bold uppercase tracking-wider text-biz-foreground-subtle">
            <ChatText size={16} weight="regular" />
            {labels.overview.pinnedNote}
          </div>
          {noteFull ? (
            <p className="mt-3 text-sm text-biz-foreground leading-relaxed whitespace-pre-wrap">
              {notePreview}
            </p>
          ) : (
            <p className="mt-3 text-sm text-biz-foreground-subtle italic">{labels.overview.noNote}</p>
          )}
        </section>
      </div>

      {/* Right sidebar — pets list */}
      <aside className="lg:col-span-4">
        <div className="rounded-biz-card border border-biz-border bg-biz-surface shadow-biz-sm">
          <div className="px-5 py-4 border-b border-biz-border">
            <h2 className="text-sm font-display font-bold uppercase tracking-wider text-biz-foreground-subtle">
              {labels.overview.petsTitle(pets.length)}
            </h2>
          </div>
          {pets.length === 0 ? (
            <div className="p-5 text-sm text-biz-foreground-subtle italic">{labels.pet.emptyBody}</div>
          ) : (
            <ul className="divide-y divide-biz-border">
              {pets.map((p) => (
                <PetSummaryRow key={p.id} pet={p} labels={labels} onClick={() => onJumpToPet?.(p.id)} />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

function Fact({
  icon,
  label,
  value,
  tone = "default",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: "default" | "primary" | "muted";
}) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-biz-foreground-subtle">
        {icon}
        {label.replace(/[:：]\s*$/, "")}
      </dt>
      <dd
        className={cn(
          "mt-1 text-sm font-medium",
          tone === "primary"
            ? "text-biz-primary"
            : tone === "muted"
              ? "text-biz-foreground-subtle italic"
              : "text-biz-foreground",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function PetSummaryRow({
  pet,
  labels,
  onClick,
}: {
  pet: PetRecord;
  labels: CustomerDetailLabels;
  onClick?: () => void;
}) {
  const IconCmp = speciesIcon[pet.species];
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-biz-muted transition-colors biz-focus-ring"
      >
        <div className="h-11 w-11 shrink-0 rounded-full bg-biz-muted flex items-center justify-center text-biz-foreground-muted">
          {pet.photoUrl ? (
            <img src={pet.photoUrl} alt="" className="h-full w-full rounded-full object-cover" />
          ) : (
            <IconCmp size={20} weight="regular" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-biz-foreground truncate">{pet.name}</div>
          <div className="text-xs text-biz-foreground-muted truncate">
            {labels.pet.species[pet.species]}
            {pet.breed ? ` · ${pet.breed}` : ""}
          </div>
        </div>
      </button>
    </li>
  );
}
